// -------------------------------------------------
// quotePricing — the ONE place that turns a NET booking-quote total into the
// selling price + agent commission shown on a card / offer / PDF.
//
// It replicates, to the cent, what makeCalculations.jsx does for a live quote
// (see the HW/BP branches: net -> selling = net / dividerFor(accountId), then
// Math.ceil, then commission = commissionOfTotal(ceiledSelling)).
//
// For a villa quoted through Rentals United (ids "G-..." and "RU-...") makeCalculations
// does one thing more: it rounds the quoted amount UP to a whole unit BEFORE the markup
// ("RU requirement: always round TOTAL up to next highest integer"), and rounds up again
// after it. Without that step here the card said ₪79,114 where the villa page and the
// booking page said ₪79,115 (Villa Tracker fix check, 12 Oct 2026). Pass `listingId` and
// the same villa gets the same figure on every screen.
// The unified quote endpoint returns a NET DTO; the search overlay and the offer
// freeze feed that DTO through HERE so every surface shows the detail page's number.
//
// HARD INVARIANT (zero-price safety): this NEVER returns 0. It returns `null` when
// the net is missing / non-positive, when FX is unusable, or when the computed
// selling would not be strictly positive — so every caller falls back to its
// already >0-guarded stored price instead of ever stamping a €0 / €0-commission.
// -------------------------------------------------
import { dividerFor, commissionOfTotal } from './pricingPolicy';

// FX table the app already keeps in localStorage ("exchange"): each row is
// { currency_code, conversion_rates } where conversion_rates = units per USD.
// Converting an amount in currency A to currency B is amt * (rateB / rateA) —
// identical to makeCalculations' getExchangeRate usage (e.g. line 560, 677).
function readExchangeRates() {
  try {
    const raw = JSON.parse(localStorage.getItem('exchange') || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function rateFor(rates, cur) {
  const row = rates.find((r) => r && r.currency_code === cur);
  const v = Number(row?.conversion_rates);
  return Number.isFinite(v) && v > 0 ? v : null;
}

/** Villas whose live quote comes through Rentals United: makeCalculations' isRUAPIIntegrated, by id. */
export const roundsQuoteUpFirst = (listingId) => /^(G-|RU-)/.test(String(listingId || ''));

/**
 * Net -> selling + commission, in the display currency.
 *
 * @param {object}  q
 * @param {number}  q.netTotal        NET total from the quote DTO (in q.quoteCurrency).
 * @param {string}  q.accountId       property account (drives dividerFor / SHM tier).
 * @param {string} [q.quoteCurrency]  currency of netTotal (the DTO `currency` field).
 * @param {string} [q.displayCurrency]currency the card/offer renders in.
 * @param {Array}  [q.exchangeRates]  override the localStorage "exchange" table (tests).
 * @param {string} [q.listingId]      the villa's id; "G-..." / "RU-..." get the RU rounding.
 * @returns {{ totalAmount:number, agencyCommission:number, netDisplay:number }|null}
 *          null whenever a positive, sellable price cannot be produced.
 */
export function applyMarkupFx({ netTotal, accountId, quoteCurrency, displayCurrency, exchangeRates, listingId } = {}) {
  const net = Number(netTotal);
  if (!Number.isFinite(net) || net <= 0) return null; // never invent a price from a bad net

  // FX: convert net from the quote's currency into the display currency. Same
  // currency (the common case — we request the quote in the display currency) or
  // an unknown code with no usable rate → factor 1 (never zero the amount).
  let netDisplay = net;
  const from = String(quoteCurrency || '').toUpperCase();
  const to = String(displayCurrency || '').toUpperCase();
  if (from && to && from !== to) {
    const rates = Array.isArray(exchangeRates) ? exchangeRates : readExchangeRates();
    const rFrom = rateFor(rates, from);
    const rTo = rateFor(rates, to);
    // Only convert when BOTH rates are known & positive; a missing rate must fall
    // back to the caller's stored price (return null), never silently mis-price.
    if (!rFrom || !rTo) return null;
    netDisplay = net * (rTo / rFrom);
    if (!Number.isFinite(netDisplay) || netDisplay <= 0) return null;
  }

  const divider = dividerFor(accountId);
  // Rentals United villas: the quoted amount is rounded up first, as on the villa page.
  const netForMarkup = roundsQuoteUpFirst(listingId) ? Math.ceil(netDisplay) : netDisplay;
  const totalAmount = Math.ceil(netForMarkup / divider);         // selling, whole units (RU/HW/BP rule)
  if (!Number.isFinite(totalAmount) || totalAmount <= 0) return null;

  // commission = 10% of the ceiled selling total, exactly
  // as makeCalculations works it out, so the figures agree.
  const agencyCommission = commissionOfTotal(totalAmount);

  return { totalAmount, agencyCommission, netDisplay };
}

export default applyMarkupFx;
